import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import useTranslation from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import TexHubLogo from '@/components/brand/TexHubLogo';
import RegionSelector from '@/components/RegionSelector';
import { Mail, Lock, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const { isUSA, syncWithUserCountry } = useLocale();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password, rememberMe);
      // Sync region with user's country BEFORE redirect
      if (user?.country) {
        const userRegion = (user.country === 'US' || user.country === 'USA') ? 'US' : 'MX';
        localStorage.setItem('texhub_region', userRegion);
      }
      // Use window.location for more reliable mobile navigation
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = (error && typeof error.message === 'string') ? error.message : (isUSA ? 'Login error' : 'Error al iniciar sesión');
      // Show error in alert for mobile compatibility
      alert(errorMsg);
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <TexHubLogo size="default" />
            </Link>
            <div className="flex items-center gap-3">
              <RegionSelector variant="minimal" showLabel={true} />
              <Link to="/register">
                <Button variant="outline" size="sm" style={{ borderColor: '#34B772', color: '#34B772' }}>
                  {isUSA ? 'Create free account' : 'Crear cuenta gratis'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
              {isUSA ? 'Log in to your account' : 'Entra a tu cuenta'}
            </h1>
            <p className="text-slate-600 mt-2">
              {isUSA ? 'Welcome back to TexHub' : 'Bienvenido de nuevo a TexHub'}
            </p>
          </div>

          <Card className="bg-white border-slate-200" data-testid="login-card">
            <CardContent className="pt-6">
              {/* Google Button First */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 mb-4"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                data-testid="google-login-btn"
              >
                {googleLoading ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                )}
                {isUSA ? 'Continue with Google' : 'Entrar con Google'}
              </Button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">{isUSA ? 'Or with email' : 'O con correo'}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{isUSA ? 'Email' : 'Correo electrónico'}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={isUSA ? 'you@company.com' : 'tu@empresa.com'}
                    className="h-12"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">{isUSA ? 'Password' : 'Contraseña'}</Label>
                    <Link to="/forgot-password" className="text-xs hover:underline" style={{ color: '#1E4C8E' }}>
                      {isUSA ? 'Forgot password?' : '¿Olvidaste tu contraseña?'}
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder={isUSA ? 'Your password' : 'Tu contraseña'}
                    className="h-12"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="rememberMe" 
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    data-testid="remember-me-checkbox"
                  />
                  <Label 
                    htmlFor="rememberMe" 
                    className="text-sm font-normal text-slate-600 cursor-pointer"
                  >
                    {isUSA ? 'Remember me on this device' : 'Recordarme en este dispositivo'}
                  </Label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 text-base font-semibold hover:opacity-90"
                  style={{ backgroundColor: '#1E4C8E' }}
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {isUSA ? 'Signing in...' : 'Entrando...'}
                    </>
                  ) : (
                    isUSA ? 'Log in to my account' : 'Entrar a mi cuenta'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-600 mt-6">
            {isUSA ? "Don't have an account? " : '¿No tienes cuenta? '}
            <Link to="/register" className="font-medium hover:underline" style={{ color: '#34B772' }}>
              {isUSA ? 'Sign up free' : 'Regístrate gratis'}
            </Link>
          </p>

          {/* Demo credentials hint */}
          <div className="mt-8 p-4 bg-slate-100 rounded-lg text-center">
            <p className="text-xs text-slate-500 mb-2">{isUSA ? 'To test the platform:' : 'Para probar la plataforma:'}</p>
            <p className="text-sm font-mono text-slate-700">demo1@demo.texhub.mx / demo123</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
